/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import AppModelMapper from './appModelMapper';
import { APP_MODE } from './appModelMapper/appJsonConstants';
import {
    DESKTOP_APP_FILTERS, PLATFORM_FILTERS, ACTION_ID, UPDATES_APP_FILTERS,
} from './appModelMapper/appModelConstants';
import { APPS_COMPONENT_ID, DISCOVER_ALL_SAPCODE_KEY } from '../enums';
import PDPs from '../../stores/pdps';
import AppConfig from '../../stores/config';
import LoggerUtil from '../LoggerUtil';

const COMPONENT_ID = 'AllAppsModelMapper';
const versionComp = (versionA, versionB) => {
    const versionAParts = (versionA || '').split('.');
    const versionBParts = (versionB || '').split('.');
    let it = 0;
    for (; it < Math.min(versionAParts.length, versionBParts.length); it += 1) {
        if (versionAParts[it] !== versionBParts[it]) {
            return (parseInt(versionAParts[it], 10) < parseInt(versionBParts[it], 10));
        }
    }
    return versionAParts.length < versionBParts.length;
};

class AllAppsModelMapper {
    // Functions for post-processing all apps model
    /* fixAppsSortOrder(allAppsList) {
        allAppsList.sort((app1, app2) => app1.sortOrder - app2.sortOrder);
    } */

    /* sortAppsBasedOnCTA(allAppsList) {
        allAppsList.forEach((app) => {
            const defaultCTAId = AppModelMapper.getDefaultCTAId(app);
            if (defaultCTAId && STATUS_SORT_ORDER_MAP.has(defaultCTAId)) {
                app.setStatusSortOrder(STATUS_SORT_ORDER_MAP.get(defaultCTAId));
            }
        });
        allAppsList.sort((app1, app2) => app1.statusSortOrder - app2.statusSortOrder);
    } */

    sortAppCardsWithMultipleVersions(allAppsList) {
        allAppsList.sort((app1, app2) => {
            if (app1.sapcode === app2.sapcode) {
                return versionComp(app1.codexVersion, app2.codexVersion) ? 1 : -1;
            }
            return 0;
        });
    }

    rearrangeIncompatibleAppTiles(allAppsModel) {
        allAppsModel.forEach((appObject) => {
            if (appObject.isIncompatible !== true) {
                return;
            }
            const otherInstalledVersions = allAppsModel.filter(
                app => ((app.sapcode === appObject.sapcode)
                && (app.appSectionFilter.includes(DESKTOP_APP_FILTERS.INSTALLED)))
            );
            if (otherInstalledVersions.length > 0) {
                const updatedFilters = appObject.appSectionFilter
                    .filter(filterValue => (
                        (filterValue !== DESKTOP_APP_FILTERS.SUBSCRIBED)
                        && (filterValue !== DESKTOP_APP_FILTERS.TRIAL)));
                updatedFilters.push(DESKTOP_APP_FILTERS.INSTALLED);
                appObject.updateAppSectionFilters(updatedFilters);
                appObject.updateSubSectionFilters(updatedFilters);
                appObject.updateSubHeadingFilters(updatedFilters);
            }
        });
    }

    updatePlatfromData(allAppsDataModel) {
        allAppsDataModel.forEach((appObject) => {
            const filteredApps = allAppsDataModel
                .filter(app => (app.appLineage === appObject.appLineage));
            const platforms = [];
            filteredApps.forEach((app) => {
                if (app.subSectionFilter.includes(PLATFORM_FILTERS.DESKTOP)
                && (!platforms.includes(PLATFORM_FILTERS.DESKTOP))) {
                    platforms.push(PLATFORM_FILTERS.DESKTOP);
                }
                if (app.subSectionFilter.includes(PLATFORM_FILTERS.MOBILE)
                && (!platforms.includes(PLATFORM_FILTERS.MOBILE))) {
                    platforms.push(PLATFORM_FILTERS.MOBILE);
                }
                if (app.subSectionFilter.includes(PLATFORM_FILTERS.WEB)
                && (!platforms.includes(PLATFORM_FILTERS.WEB))) {
                    platforms.push(PLATFORM_FILTERS.WEB);
                }
            });
            AppModelMapper.getDefaultField(appObject).setAvailablePlatforms(platforms);
        });
    }

    removeDuplicates(arrayToFilter, prop) {
        return arrayToFilter.filter(
            (obj, pos, arr) => arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos
        );
    }

    fixDropDownOrderList(dropDownList) {
        const sortedApplicableActions = [
            ACTION_ID.OPEN,
            ACTION_ID.LEARN_MORE,
            ACTION_ID.BUY_NOW,
            ACTION_ID.LIST_OTHER_VERSIONS,
            ACTION_ID.UNINSTALL,
        ];
        // eslint-disable-next-line no-param-reassign
        dropDownList = dropDownList.filter(
            dropDownItem => sortedApplicableActions.indexOf(dropDownItem.actionId) !== -1
        );
        dropDownList.sort(
            (
                item1, item2
            ) => sortedApplicableActions.indexOf(item1.actionId)
            - sortedApplicableActions.indexOf(item2.actionId)
        );
        return dropDownList;
    }

    mergeActionList(targetAppModelObject, appsListToMerge) {
        let mergedActionList = [];
        appsListToMerge.forEach((appToMerge) => {
            mergedActionList = [...mergedActionList, ...appToMerge.actionList];
        });
        // eslint-disable-next-line no-param-reassign
        targetAppModelObject.actionList = this.removeDuplicates(mergedActionList, 'actionId');

        const targetDefaultField = AppModelMapper.getDefaultField(targetAppModelObject);
        let mergedDropdownList = [...targetDefaultField.dropDownList];
        appsListToMerge.forEach((appToMerge) => {
            const defaultField = AppModelMapper.getDefaultField(appToMerge);
            if (defaultField) {
                mergedDropdownList = [
                    ...mergedDropdownList, ...defaultField.dropDownList, ...defaultField.buttonList,
                ];
            }
        });
        mergedDropdownList = this.removeDuplicates(mergedDropdownList, 'actionId');
        mergedDropdownList = this.fixDropDownOrderList(mergedDropdownList);
        targetDefaultField.dropDownList = mergedDropdownList;
    }

    removeOtherVersionsFromActiveSessions(allAppsModel) {
        const appsInSession = allAppsModel
            .filter(app => app.progress && (app.progress.inProgress || app.progress.isWaiting));
        const sapcodesInSession = appsInSession.map(app => app.sapcode);
        allAppsModel.forEach((app) => {
            if (sapcodesInSession.includes(app.sapcode)) {
                AppModelMapper.removeListOtherVersions(app);
            }
        });
    }

    getLatestVersionOfInstalledAppForSapcode(allAppsModel, sapCode) {
        let latestVersion = '0.0.0';
        allAppsModel.forEach((app) => {
            if (app.sapcode === sapCode && !(app.isFullAppUpdate()
                && app.mode !== APP_MODE.ACTIVE_SESSIONS)) {
                if (versionComp(latestVersion, app.codexVersion)) {
                    latestVersion = app.codexVersion;
                }
            }
        });
        return latestVersion;
    }

    updateDiscoverStatusforProducts(allAppsModel, discoverProdList = []) {
        allAppsModel.forEach((app) => {
            if (discoverProdList.includes(app.sapcode)
                || discoverProdList.includes(DISCOVER_ALL_SAPCODE_KEY)) {
                app.setDiscoverEnabled(true);
                app.updateActionHiddenStatus(ACTION_ID.DISCOVER, false);
            } else {
                app.updateActionHiddenStatus(ACTION_ID.DISCOVER, true);
                app.setDiscoverEnabled(false);
            }
        });
    }

    processAllAppsModel(allAppsModel, discoverProdList) {
        this.updatePlatfromData(allAppsModel);

        // this.rearrangeIncompatibleAppTiles(allAppsModel);

        this.removeOtherVersionsFromActiveSessions(allAppsModel);

        this.sortAppCardsWithMultipleVersions(allAppsModel);

        this.updateDiscoverStatusforProducts(allAppsModel, discoverProdList);

        return allAppsModel;
    }

    isValidMode(mode) {
        if (mode === APP_MODE.AVAILABLE_TO_INSTALL
        || mode === APP_MODE.AVAILABLE_UPDATES
        || mode === APP_MODE.INSTALLED
        || mode === APP_MODE.INCOMPATIBLE_APPS
        || mode === APP_MODE.ACTIVE_SESSIONS
        || mode === APP_MODE.WEB_APPS
        || mode === APP_MODE.MOBILE_APPS) {
            return true;
        }
        return false;
    }

    /**
     * getAllAppsModel maps a json object from Apps BL to
     * @param  {} allAppsData
     */
    getAllAppsModel(allAppsData, onAppAction, controllerData, isDiscoverTabEnabled = false) {
        let allAppsDataModel = [];
        const PDPListData = controllerData ? controllerData.PDPListData : null;
        const discoverProdList = controllerData ? controllerData.discoverProdList : [];

        const pdpLayouts = (AppConfig.layouts && AppConfig.layouts.pdp) || {};
        const pdpSapCodesList = PDPListData ? PDPListData.reduce((sapCodeList, pdpItem) => {
            if (pdpItem && pdpItem.sapCode
                && (!pdpItem.layoutID || pdpLayouts[pdpItem.layoutID])) {
                sapCodeList.push(pdpItem.sapCode.trim());
            }
            return sapCodeList;
        }, []) : null;

        Object.keys(allAppsData).forEach((mode) => {
            if (this.isValidMode(mode) && Array.isArray(allAppsData[mode])) {
                allAppsData[mode].forEach((appData) => {
                    let discoverEnabled;
                    if (Object.prototype.hasOwnProperty.call(appData, 'discover_enabled')) {
                        discoverEnabled = isDiscoverTabEnabled && appData.discover_enabled;
                    }
	                LoggerUtil.debug(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: getAllAppsModel: ${mode}-${JSON.stringify(appData)}`);
                    const appModel = AppModelMapper.toAppModel({
                        mode: mode.toLowerCase(),
                        appData: { ...appData, discover_enabled: discoverEnabled },
                        onAppAction,
                    });
                    allAppsDataModel.push(appModel);
                });
            } else {
                LoggerUtil.info(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: getAllAppsModel: Ignoring mode ${mode}`);
            }
        });

        const filteredAllAppsModel = (controllerData && controllerData.showEOL)
            ? allAppsDataModel : allAppsDataModel.filter(
                app => (!app.isEOLApp || app.mode === APP_MODE.AVAILABLE_UPDATES
                    || app.mode === APP_MODE.ACTIVE_SESSIONS || app.mode === APP_MODE.INSTALLED)
            );
        allAppsDataModel = filteredAllAppsModel;

        const processedAllAppsModel = this.processAllAppsModel(allAppsDataModel, discoverProdList);
        // TODO(tayal): move these to post processing.
        if (pdpSapCodesList) {
            this.refreshLearnMore(pdpSapCodesList, processedAllAppsModel);
        }
        this.updateAppsLanguageInfo(PDPListData, processedAllAppsModel);

        processedAllAppsModel.forEach((appModel) => {
            AppModelMapper.updatePDPModel(appModel, processedAllAppsModel);
        });

        return processedAllAppsModel;
    }

    refreshLearnMore(pdpSapCodesList, allAppsDataModel) {
        allAppsDataModel.forEach((appModel) => {
            const appSapCode = appModel.sapcode;
            if (pdpSapCodesList.includes(appSapCode)) {
                appModel.setPDPAvailabilityStatus(true);
                AppModelMapper.showLearnMore(appModel);
            } else {
                appModel.setPDPAvailabilityStatus(false);
                AppModelMapper.hideLearnMore(appModel);
            }
        });
    }

    updateAppsLanguageInfo(PDPListData, allAppsDataModel) {
        if (PDPListData && PDPListData.length) {
            PDPListData.forEach((pdpItem) => {
                if (pdpItem.supportedLanguages
                    && pdpItem.supportedLanguages.length
                    && pdpItem.sapCode) {
                    PDPs.setAppLanguages(pdpItem.sapCode.trim(), pdpItem.supportedLanguages
                        .map(language => language.languageName));
                }
            });
            allAppsDataModel.forEach((appModel) => {
                const appSapCode = appModel.sapcode;
                if (PDPs.getAppLanguages(appSapCode)) {
                    appModel.setLanguages(PDPs.getAppLanguages(appSapCode));
                }
            });
        }
    }

    getUpdatesCount(allAppsModel) {
        const updatesAvailable = allAppsModel
            .filter(app => (app.subHeadingFilter
                .includes(UPDATES_APP_FILTERS.NEW_UPDATES)
                && (app.progress === null)
                && (!app.isHidden)));
        LoggerUtil.info(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: getUpdatesCount: Updates Count ${updatesAvailable.length}`);
        return updatesAvailable.length;
    }
}
export default new AllAppsModelMapper();
